/**
 * Visualization Registry
 *
 * Single source of truth for visualization type classification, component
 * resolution, size constraints, capabilities, and prop adapters.
 *
 * Usage:
 *   import { resolveEntry, getComponent, getConstraints } from '@/components/visualizations/visualizationRegistry';
 *
 *   const entry = resolveEntry({ key: 'sankey/default' });
 *   const component = getComponent(entry);
 *   const constraints = getConstraints(entry);
 */

import { defineAsyncComponent } from 'vue';

// ── Canonical type constants ─────────────────────────────────────────────────

export const VIZ_TYPE = Object.freeze({
  GRAPH: 'graph',
  KPI: 'kpi',
  MAP: 'map',
  SIMILARITY_MATRIX: 'similarity-matrix',
  SANKEY: 'sankey',
  ORDERBOOK: 'orderbook',
  TABLE: 'table',
});

export const VIZ_SUBTYPE = Object.freeze({
  // graph
  TIMESERIES: 'timeseries',
  CATEGORICAL: 'categorical',
  HEATMAP: 'heatmap',
  SUMMARY_BARS: 'summary-bars',
  PERIOD_COMPARISON: 'period-comparison',
  // kpi
  SINGLE: 'single',
  GROUPED: 'grouped',
  // map
  FLOW: 'flow',
  FIXED: 'fixed',
  TRACKER: 'tracker',
  CHOROPLETH: 'choropleth',
  CONGESTION: 'congestion',
  // similarity-matrix
  CORRELATION: 'correlation',
  // orderbook
  DEPTH: 'depth',
  PRICES: 'prices',
  // table
  SUMMARY: 'summary',
});

export const VIZ_SCOPE = Object.freeze({
  EUROPE: 'europe',
  WORLD: 'world',
  REGIONAL: 'regional',
  NETHERLANDS: 'netherlands',
});

// ── Component factories ──────────────────────────────────────────────────────

function lazyDatasetChart() {
  return defineAsyncComponent(() =>
    import('@/components/visualizations/graph/GraphVisualization.vue')
  );
}
function lazyDatasetKpiCard() {
  return defineAsyncComponent(() => import('@/components/visualizations/kpi/KpiVisualization.vue'));
}
function lazyDatasetSimilarityMatrix() {
  return defineAsyncComponent(() =>
    import('@/components/visualizations/similarity-matrix/SimilarityMatrixVisualization.vue')
  );
}
function lazyDatasetWorldMap() {
  return defineAsyncComponent(() => import('@/components/visualizations/map/ChoroplethMap.vue'));
}
function lazyEuropeFlowMap() {
  return defineAsyncComponent(() =>
    import('@/components/visualizations/map/EuropeElectricityFlowMap.vue')
  );
}
function lazyWaterRiskMap() {
  return defineAsyncComponent(() =>
    import('@/components/visualizations/map/WaterRiskVisualization.vue')
  );
}
function lazyGasFlowMap() {
  return defineAsyncComponent(() => import('@/components/visualizations/map/EuropeGasFlowMap.vue'));
}
function lazyGasBalanceSankey() {
  return defineAsyncComponent(() =>
    import('@/components/visualizations/sankey/SankeyVisualization.vue')
  );
}
function lazyLngCarrierMap() {
  return defineAsyncComponent(() =>
    import('@/components/visualizations/map/LngCarrierTrackerMap.vue')
  );
}
function lazyOrderbookDepth() {
  return defineAsyncComponent(() =>
    import('@/features/orderbook/components/OrderbookDepthPanel.vue')
  );
}
function lazyOrderbookPrices() {
  return defineAsyncComponent(() =>
    import('@/features/orderbook/components/OrderbookPricesVisualization.vue')
  );
}

// ── Registry entries ─────────────────────────────────────────────────────────
//
// Each entry describes one concrete visualization variant:
//
//   key            – canonical registry key: '{type}/{subtype}[/{scope}]'
//   type           – VIZ_TYPE constant
//   subtype        – VIZ_SUBTYPE constant (null when family has only one variant)
//   scope          – VIZ_SCOPE constant (null for non-geographic types)
//   componentName  – target Vue component name
//   getComponent   – factory returning the lazy-loaded component
//   constraints    – grid sizing { minW, minH, defaultW, defaultH }
//   capabilities   – list of supported feature strings
//   chromePolicy   – panel chrome behaviour: 'standard' | 'kpi' | 'none'
//   renderingMode  – 'via-renderer': goes through DatasetVisualizationRenderer
//                    'direct': rendered directly by DashboardPanel
//   adaptProps     – (standardProps) => componentProps adapter
//   builder        – custom-builder metadata for guided semantic authoring
//

const ENTRIES = [
  // ── graph / timeseries ──────────────────────────────────────────────────
  {
    key: 'graph/timeseries',
    type: VIZ_TYPE.GRAPH,
    subtype: VIZ_SUBTYPE.TIMESERIES,
    scope: null,
    componentName: 'GraphVisualization',
    getComponent: lazyDatasetChart,
    constraints: { minW: 8, minH: 6, defaultW: 12, defaultH: 8 },
    capabilities: [
      'resize',
      'embed',
      'legend',
      'zoom',
      'filters',
      'date-range',
      'export',
      'sync',
      'indicators',
      'fullscreen',
    ],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    builder: {
      supported: true,
      kind: 'graph',
      allowMultipleSeries: true,
      compatibleShapes: ['timeseries'],
      requiredSections: ['series_bindings', 'axes', 'time_controls'],
      starterDefaults: {
        graphStyle: 'line',
        dateRange: 'last_7d',
        granularity: 'auto',
      },
    },
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      eventScope: p.eventScope,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      styleOverrides: p.styleOverrides,
      ui: p.ui,
    }),
  },

  // ── graph / categorical ─────────────────────────────────────────────────
  {
    key: 'graph/categorical',
    type: VIZ_TYPE.GRAPH,
    subtype: VIZ_SUBTYPE.CATEGORICAL,
    scope: null,
    componentName: 'GraphVisualization',
    getComponent: lazyDatasetChart,
    constraints: { minW: 8, minH: 6, defaultW: 10, defaultH: 6 },
    capabilities: [
      'resize',
      'embed',
      'legend',
      'zoom',
      'filters',
      'date-range',
      'export',
      'sync',
      'fullscreen',
    ],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    builder: {
      supported: true,
      kind: 'graph',
      allowMultipleSeries: true,
      compatibleShapes: ['categorical'],
      requiredSections: ['series_bindings', 'axes', 'time_controls'],
      starterDefaults: {
        graphStyle: 'bar',
        dateRange: 'last_7d',
        granularity: 'auto',
      },
    },
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      eventScope: p.eventScope,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      styleOverrides: p.styleOverrides,
      ui: p.ui,
    }),
  },

  // ── graph/summary-bars (report layout: numbers printed in the chart) ──────────────
  {
    key: 'graph/summary-bars',
    type: VIZ_TYPE.GRAPH,
    subtype: VIZ_SUBTYPE.SUMMARY_BARS,
    scope: null,
    componentName: 'GraphVisualization',
    getComponent: lazyDatasetChart,
    constraints: { minW: 8, minH: 6, defaultW: 12, defaultH: 9 },
    capabilities: ['resize', 'embed', 'filters', 'date-range', 'export', 'sync', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    builder: { supported: false },
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      eventScope: p.eventScope,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      styleOverrides: p.styleOverrides,
      ui: p.ui,
    }),
  },

  // ── graph/period-comparison (report layout: numbers printed in the chart) ──────────────
  {
    key: 'graph/period-comparison',
    type: VIZ_TYPE.GRAPH,
    subtype: VIZ_SUBTYPE.PERIOD_COMPARISON,
    scope: null,
    componentName: 'GraphVisualization',
    getComponent: lazyDatasetChart,
    constraints: { minW: 8, minH: 6, defaultW: 12, defaultH: 6 },
    capabilities: ['resize', 'embed', 'filters', 'date-range', 'export', 'sync', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    builder: { supported: false },
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      eventScope: p.eventScope,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      styleOverrides: p.styleOverrides,
      ui: p.ui,
    }),
  },

  // ── graph / heatmap ─────────────────────────────────────────────────────
  {
    key: 'graph/heatmap',
    type: VIZ_TYPE.GRAPH,
    subtype: VIZ_SUBTYPE.HEATMAP,
    scope: null,
    componentName: 'GraphVisualization',
    getComponent: lazyDatasetChart,
    constraints: { minW: 10, minH: 8, defaultW: 12, defaultH: 8 },
    capabilities: [
      'resize',
      'embed',
      'filters',
      'date-range',
      'export',
      'sync',
      'fullscreen',
    ],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    builder: {
      supported: true,
      kind: 'graph',
      allowMultipleSeries: true,
      compatibleShapes: ['timeseries'],
      requiredSections: ['series_bindings', 'heatmap', 'time_controls'],
      starterDefaults: {
        dateRange: 'last_30d',
        granularity: '1h',
      },
    },
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      eventScope: p.eventScope,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      styleOverrides: p.styleOverrides,
      ui: p.ui,
    }),
  },

  // ── kpi / single ────────────────────────────────────────────────────────
  {
    key: 'kpi/single',
    type: VIZ_TYPE.KPI,
    subtype: VIZ_SUBTYPE.SINGLE,
    scope: null,
    componentName: 'KpiVisualization',
    getComponent: lazyDatasetKpiCard,
    constraints: { minW: 2, minH: 1, defaultW: 8, defaultH: 4 },
    capabilities: ['resize', 'embed', 'fullscreen'],
    chromePolicy: 'kpi',
    renderingMode: 'via-renderer',
    builder: {
      supported: true,
      kind: 'kpi',
      allowMultipleSeries: false,
      compatibleShapes: ['timeseries'],
      requiredSections: ['series_bindings', 'time_controls'],
      starterDefaults: {
        dateRange: 'today',
        granularity: 'auto',
        transform: 'last_value',
      },
    },
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      kpiQuery: p.kpiQuery,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      renderOptions: p.renderOptions,
      ui: p.ui,
    }),
  },

  // ── kpi / grouped ───────────────────────────────────────────────────────
  {
    key: 'kpi/grouped',
    type: VIZ_TYPE.KPI,
    subtype: VIZ_SUBTYPE.GROUPED,
    scope: null,
    componentName: 'KpiVisualization',
    getComponent: lazyDatasetKpiCard,
    constraints: { minW: 8, minH: 4, defaultW: 24, defaultH: 4 },
    capabilities: ['resize', 'embed', 'fullscreen'],
    chromePolicy: 'kpi',
    renderingMode: 'via-renderer',
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      kpiQuery: p.kpiQuery,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      ui: p.ui,
    }),
  },

  // ── similarity-matrix / correlation ─────────────────────────────────────
  {
    key: 'similarity-matrix/correlation',
    type: VIZ_TYPE.SIMILARITY_MATRIX,
    subtype: VIZ_SUBTYPE.CORRELATION,
    scope: null,
    componentName: 'SimilarityMatrixVisualization',
    getComponent: lazyDatasetSimilarityMatrix,
    constraints: { minW: 10, minH: 8, defaultW: 24, defaultH: 8 },
    capabilities: ['resize', 'embed', 'filters', 'date-range', 'export', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      ui: p.ui,
    }),
  },

  // ── sankey / default ────────────────────────────────────────────────────
  {
    key: 'sankey/default',
    type: VIZ_TYPE.SANKEY,
    subtype: null,
    scope: null,
    componentName: 'SankeyVisualization',
    getComponent: lazyGasBalanceSankey,
    constraints: { minW: 10, minH: 8, defaultW: 24, defaultH: 10 },
    capabilities: ['resize', 'embed', 'export', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    adaptProps: (p) => ({
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      labelOverrides: p.labelOverrides,
      ui: p.ui,
    }),
  },

  // ── map / flow / europe — electricity ───────────────────────────────────
  {
    key: 'map/flow/europe-electricity',
    type: VIZ_TYPE.MAP,
    subtype: VIZ_SUBTYPE.FLOW,
    scope: VIZ_SCOPE.EUROPE,
    componentName: 'EuropeElectricityFlowMap',
    getComponent: lazyEuropeFlowMap,
    constraints: { minW: 6, minH: 8, defaultW: 14, defaultH: 10 },
    capabilities: ['resize', 'embed', 'filters', 'export', 'sync', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      eventScope: p.eventScope,
      labelOverrides: p.labelOverrides,
      ui: p.ui,
    }),
  },

  // ── map / fixed / europe — water risk ───────────────────────────────────
  {
    key: 'map/fixed/europe',
    type: VIZ_TYPE.MAP,
    subtype: VIZ_SUBTYPE.FIXED,
    scope: VIZ_SCOPE.EUROPE,
    componentName: 'WaterRiskVisualization',
    getComponent: lazyWaterRiskMap,
    constraints: { minW: 6, minH: 8, defaultW: 14, defaultH: 10 },
    capabilities: ['resize', 'embed', 'filters', 'export', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      ui: p.ui,
      footerOptions: p.footerOptions,
    }),
  },

  // ── map / flow / europe — gas ──────────────────────────────────────────
  {
    key: 'map/flow/europe-gas',
    type: VIZ_TYPE.MAP,
    subtype: VIZ_SUBTYPE.FLOW,
    scope: VIZ_SCOPE.EUROPE,
    componentName: 'EuropeGasFlowMap',
    getComponent: lazyGasFlowMap,
    constraints: { minW: 6, minH: 8, defaultW: 14, defaultH: 10 },
    capabilities: ['resize', 'embed', 'filters', 'export', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    adaptProps: (p) => ({
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      labelOverrides: p.labelOverrides,
      ui: p.ui,
    }),
  },

  // ── map / flow / europe — generic (commodity-agnostic) ──────────────────
  // TODO: replace getComponent with a unified EuropeFlowMap that handles both
  //       electricity and gas flows based on the dataset/series commodity.
  //       This is the canonical key produced by YAML configs: type=map, subtype=flow, scope=europe.
  {
    key: 'map/flow/europe',
    type: VIZ_TYPE.MAP,
    subtype: VIZ_SUBTYPE.FLOW,
    scope: VIZ_SCOPE.EUROPE,
    componentName: 'EuropeElectricityFlowMap',
    getComponent: lazyEuropeFlowMap,
    constraints: { minW: 6, minH: 8, defaultW: 14, defaultH: 10 },
    capabilities: ['resize', 'embed', 'filters', 'export', 'sync', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      eventScope: p.eventScope,
      labelOverrides: p.labelOverrides,
      ui: p.ui,
    }),
  },

  // ── map / choropleth / world ───────────────────────────────────────────
  {
    key: 'map/choropleth/world',
    type: VIZ_TYPE.MAP,
    subtype: VIZ_SUBTYPE.CHOROPLETH,
    scope: VIZ_SCOPE.WORLD,
    componentName: 'WorldChoroplethMap',
    getComponent: lazyDatasetWorldMap,
    constraints: { minW: 6, minH: 8, defaultW: 14, defaultH: 10 },
    capabilities: ['resize', 'embed', 'export', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    adaptProps: (p) => ({
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      ui: p.ui,
    }),
  },

  // ── map / tracker / world ──────────────────────────────────────────────
  {
    key: 'map/tracker/world',
    type: VIZ_TYPE.MAP,
    subtype: VIZ_SUBTYPE.TRACKER,
    scope: VIZ_SCOPE.WORLD,
    componentName: 'LngCarrierTrackerMap',
    getComponent: lazyLngCarrierMap,
    constraints: { minW: 10, minH: 8, defaultW: 14, defaultH: 10 },
    capabilities: ['resize', 'embed', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'direct', // rendered directly — no datasetId/visualizationId
    adaptProps: (p) => ({
      showOriginDestFilter: false,
      ui: p.ui,
    }),
  },

  // ── orderbook / depth ──────────────────────────────────────────────────
  // Live order book panel: polls /api/v2/tools/orderbook/books every 3 s.
  // Renders bid/ask depth chart, price ladder, and spread chart via tabs.
  // Does NOT use datasetId — receives deliveryArea + product from controlOverrides.
  {
    key: 'orderbook/depth',
    type: VIZ_TYPE.ORDERBOOK,
    subtype: VIZ_SUBTYPE.DEPTH,
    scope: null,
    componentName: 'OrderbookDepthPanel',
    getComponent: lazyOrderbookDepth,
    constraints: { minW: 8, minH: 8, defaultW: 12, defaultH: 10 },
    capabilities: ['resize', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'direct', // no datasetId/visualizationId
    adaptProps: (p) => ({
      deliveryArea: p.controlOverrides?.deliveryArea ?? '10YNL----------L',
      product: p.controlOverrides?.product ?? null,
      height: p.ui?.height ?? 400,
      ui: p.ui,
    }),
  },

  // ── orderbook / prices ─────────────────────────────────────────────────
  // Dataset-backed time-series chart for historical order book price data
  // (best bid, best ask, spread, last price). Backed by epex-m7-orderbook-prices
  // or any dataset with compatible bid/ask/spread series.
  {
    key: 'orderbook/prices',
    type: VIZ_TYPE.ORDERBOOK,
    subtype: VIZ_SUBTYPE.PRICES,
    scope: null,
    componentName: 'OrderbookPricesVisualization',
    getComponent: lazyOrderbookPrices,
    constraints: { minW: 8, minH: 6, defaultW: 12, defaultH: 8 },
    capabilities: ['resize', 'embed', 'legend', 'zoom', 'filters', 'date-range', 'export', 'sync', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      eventScope: p.eventScope,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      styleOverrides: p.styleOverrides,
      ui: p.ui,
    }),
  },

  // ── table / summary ─────────────────────────────────────────────────────
  // Rows from a dimension, columns from bindings. Rendered by GraphVisualization, which
  // swaps the graph for the table body and keeps the card: header, KPI tiles, filters,
  // date range, footer actions and states.
  {
    key: 'table/summary',
    type: VIZ_TYPE.TABLE,
    subtype: VIZ_SUBTYPE.SUMMARY,
    scope: null,
    componentName: 'GraphVisualization',
    getComponent: lazyDatasetChart,
    constraints: { minW: 8, minH: 5, defaultW: 24, defaultH: 9 },
    capabilities: ['resize', 'embed', 'filters', 'date-range', 'export', 'sync', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    builder: { supported: false },
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      eventScope: p.eventScope,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      styleOverrides: p.styleOverrides,
      ui: p.ui,
    }),
  },

  // ── map / congestion / netherlands ──────────────────────────────────────
  // Congestion areas on a map of the Netherlands, with KPI tiles and a collapsible table.
  // Rendered by GraphVisualization like `table/summary`: the card stays, the body changes.
  {
    key: 'map/congestion/netherlands',
    type: VIZ_TYPE.MAP,
    subtype: VIZ_SUBTYPE.CONGESTION,
    scope: VIZ_SCOPE.NETHERLANDS,
    componentName: 'GraphVisualization',
    getComponent: lazyDatasetChart,
    constraints: { minW: 8, minH: 10, defaultW: 24, defaultH: 16 },
    capabilities: ['resize', 'embed', 'filters', 'date-range', 'export', 'sync', 'fullscreen'],
    chromePolicy: 'standard',
    renderingMode: 'via-renderer',
    builder: { supported: false },
    adaptProps: (p) => ({
      chartId: p.chartId,
      datasetId: p.datasetId,
      visualizationId: p.visualizationId,
      eventScope: p.eventScope,
      labelOverrides: p.labelOverrides,
      controlOverrides: p.controlOverrides,
      styleOverrides: p.styleOverrides,
      ui: p.ui,
    }),
  },
];

// ── Type string → registry key mapping ───────────────────────────────────────
// Maps visualizationType strings (from YAML configs) and panel-type strings
// (from stored panel definitions) to canonical registry keys.
const VIZ_TYPE_STRING_TO_KEY = {
  // Canonical short-form aliases (legacy / convenience)
  timeseries: 'graph/timeseries',
  categorical: 'graph/categorical',
  bar: 'graph/categorical',
  ohlc: 'graph/timeseries',
  graph: 'graph/timeseries',
  kpi: 'kpi/single',
  kpi_group: 'kpi/grouped',
  worldmap: 'map/choropleth/world',
  map: 'map/flow/europe',
  sankey: 'sankey/default',
  similarity_matrix: 'similarity-matrix/correlation',
  'similarity-matrix': 'similarity-matrix/correlation',
  matrix: 'similarity-matrix/correlation',
  heatmap: 'graph/heatmap',
  // Partial canonical keys that may appear in old stored panel data
  'map/flow': 'map/flow/europe',
  'map/fixed': 'map/fixed/europe',
  'map/choropleth': 'map/choropleth/world',
  'map/tracker': 'map/tracker/world',
  'map/congestion': 'map/congestion/netherlands',
  'sankey/default': 'sankey/default',
  'similarity-matrix/correlation': 'similarity-matrix/correlation',
  'graph/heatmap': 'graph/heatmap',
  'graph/summary-bars': 'graph/summary-bars',
  'graph/period-comparison': 'graph/period-comparison',
  table: 'table/summary',
  'table/summary': 'table/summary',

  // Panel-type strings used by existing panel definitions (CategoryDashboard, etc.)
  datasetchart: 'graph/timeseries',
  dataset_visualization: 'graph/timeseries',
  dataset_chart: 'graph/timeseries',
  datasetkpicard: 'kpi/single',
  dataset_kpi_card: 'kpi/single',
  datasetsimilaritymatrix: 'similarity-matrix/correlation',
  dataset_similarity_matrix: 'similarity-matrix/correlation',
  dataseteuropemap: 'map/flow/europe',
  dataset_europe_map: 'map/flow/europe',
  waterriskmap: 'map/fixed/europe',
  water_risk_map: 'map/fixed/europe',
  europeflowmap: 'map/flow/europe',
  datasetworldmap: 'map/choropleth/world',
  dataset_world_map: 'map/choropleth/world',
  gasbalancesankey: 'sankey/default',
  gas_balance_sankey: 'sankey/default',
  gasflowmap: 'map/flow/europe-gas',
  gas_flow_map: 'map/flow/europe-gas',
  lngcarriermap: 'map/tracker/world',
  lng_carrier_map: 'map/tracker/world',
  // orderbook panel type strings
  orderbookdepth: 'orderbook/depth',
  orderbook_depth: 'orderbook/depth',
  orderbookprices: 'orderbook/prices',
  orderbook_prices: 'orderbook/prices',
  'orderbook/depth': 'orderbook/depth',
  'orderbook/prices': 'orderbook/prices',
};

// ── Internal lookup table ────────────────────────────────────────────────────

const _byKey = new Map();

for (const entry of ENTRIES) {
  _byKey.set(entry.key, entry);
}

// ── Component caching ────────────────────────────────────────────────────────
const _componentCache = Symbol('componentCache');

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Resolve a registry entry from a canonical key or a visualizationType string.
 *
 * Resolution order:
 *   1. Explicit canonical key (e.g. 'graph/timeseries')
 *   2. visualizationType string from backend YAML (e.g. 'timeseries', 'map')
 *   3. Legacy key string mapping (e.g. 'dataset_visualization' → 'graph/timeseries')
 *   4. Default fallback: graph/timeseries
 *
 * @param {object} opts
 * @param {string} [opts.key] - Canonical registry key
 * @param {string} [opts.visualizationType] - Backend YAML type string
 * @returns {object} registry entry (never null)
 */
export function resolveEntry({ key, visualizationType } = {}) {
  return resolveEntryOrNull({ key, visualizationType }) ?? _byKey.get('graph/timeseries');
}

/**
 * Same as resolveEntry but returns null when nothing matches.
 *
 * @param {object} opts
 * @returns {object|null}
 */
export function resolveEntryOrNull({ key, visualizationType } = {}) {
  // 1. Canonical key lookup (exact match on registry key)
  if (key) {
    const found = _byKey.get(key);
    if (found) return found;
  }

  // 2. Explicit visualizationType string (checked before legacy key mapping
  //    so that e.g. key='dataset_visualization' + visualizationType='map'
  //    resolves to the map entry, not graph/timeseries)
  if (visualizationType) {
    const vt = String(visualizationType)
      .trim()
      .toLowerCase();
    // 2a. Try as canonical key directly (handles new API format: "graph/timeseries")
    const asKey = _byKey.get(vt);
    if (asKey) return asKey;
    // 2b. Fall back to legacy string mapping (handles old format: "timeseries", "kpi_group")
    const mappedKey = VIZ_TYPE_STRING_TO_KEY[vt];
    if (mappedKey) return _byKey.get(mappedKey) ?? null;
  }

  // 3. Try key as a type string (handles old panel types like 'datasetChart')
  if (key) {
    const normalized = String(key)
      .trim()
      .toLowerCase();
    const mappedFromKey = VIZ_TYPE_STRING_TO_KEY[normalized];
    if (mappedFromKey) return _byKey.get(mappedFromKey) ?? null;
  }

  return null;
}

/**
 * Resolve the most specific canonical registry key represented by a catalog
 * visualization object. Geographic maps need all three fields so a fixed map
 * cannot be mistaken for the generic Europe flow map.
 *
 * @param {object} visualization
 * @returns {string}
 */
export function visualizationKeyFromConfig(visualization = {}) {
  const explicitKey = String(visualization.key || '').trim().toLowerCase();
  if (explicitKey && _byKey.has(explicitKey)) return explicitKey;

  const type = String(visualization.type || '').trim().toLowerCase();
  const subtype = String(visualization.subtype || '').trim().toLowerCase();
  const scope = String(visualization.scope || '').trim().toLowerCase();
  const candidates = [
    type && subtype && scope ? `${type}/${subtype}/${scope}` : '',
    type && subtype ? `${type}/${subtype}` : '',
    type,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const entry = resolveEntryOrNull({ key: candidate, visualizationType: candidate });
    if (entry) return entry.key;
  }
  return resolveEntry({ visualizationType: type }).key;
}

/**
 * Return the async component for an entry. Cached after first call.
 *
 * @param {object|null} entry
 * @returns {object|null} Vue async component
 */
export function getComponent(entry) {
  if (!entry) return null;
  if (!entry[_componentCache]) {
    entry[_componentCache] = entry.getComponent();
  }
  return entry[_componentCache];
}

/**
 * Adapt standard visualization props to the props expected by the specific
 * component. The input shape is the superset accepted by DatasetVisualizationRenderer.
 *
 * @param {object|null} entry
 * @param {object} standardProps
 * @returns {object} component-specific props
 */
export function adaptProps(entry, standardProps) {
  if (!entry || !standardProps) return {};
  return entry.adaptProps(standardProps);
}

/**
 * Return grid size constraints for an entry.
 *
 * @param {object|null} entry
 * @returns {{ minW: number, minH: number, defaultW: number, defaultH: number }}
 */
export function getConstraints(entry) {
  if (!entry) return { minW: 8, minH: 6, defaultW: 12, defaultH: 8 };
  return entry.constraints;
}

/**
 * Return all registered entries. Useful for tooling and tests.
 *
 * @returns {object[]}
 */
export function getAllEntries() {
  return [...ENTRIES];
}

export function getBuilderConfig(source) {
  const entry =
    typeof source === 'string'
      ? resolveEntryOrNull({ key: source, visualizationType: source })
      : source;

  return entry?.builder || null;
}

export function isBuilderSupported(source) {
  return Boolean(getBuilderConfig(source)?.supported);
}
